import {
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  Skeleton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Flex,
  Button,
  useDisclosure,
  Box,
  Center,
  Heading,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { FileWithPath, useDropzone } from 'react-dropzone';

import {
  utils as commonUtils,
  finleyAssert,
} from '@finley-technologies/common';

import {
  GetCreditFacilityQueryGQLType,
  ReceivableBorrowingBaseTestWaiverGQLType,
  ReceivableBorrowingBaseTestWaiverInputGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  useGetReceivableWaiversForCreditFacilityWithOrganizationQuery,
  useUpdateReceivableWaiversMutation,
} from 'packages/borrowing-base/requests/borrowingBase.gql.generated';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  errorToastAdminConfig,
  errorToastId,
  successToastConfig,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import {
  getUnexpectedErrorMessage,
  truncateUploadFileName,
} from 'packages/common-frontend/utils';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useConvertReceivableBorrowingBaseWaiversWithNewIdsLazyQueryGQLType } from 'packages/loanProduct/requests/requests.gql.generated';
import { FiDownload, FiUpload } from 'react-icons/fi';

type ExportWaiversButtonProps = {
  creditFacilityId: string;
};
const ExportWaiversButton = ({
  creditFacilityId,
}: ExportWaiversButtonProps) => {
  const toast = useToast();
  const { data: creditFacilityData } = useGetCreditFacilityQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });

  const {
    data: waiversData,
  } = useGetReceivableWaiversForCreditFacilityWithOrganizationQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });

  const downloadWaiversWithEntitiesJson = useCallback(
    ({
      waivers,
      creditFacility,
    }: {
      waivers: ReceivableBorrowingBaseTestWaiverGQLType[];
      creditFacility: GetCreditFacilityQueryGQLType;
    }) => {
      const loanProductSlugMap: {
        [loanProductId: string]: {
          loanProductId: string;
          loanProductSlug: string;
        };
      } = {};
      _.forEach(creditFacility.creditFacility.loanProducts, (loanProduct) => {
        loanProductSlugMap[loanProduct.id] = {
          loanProductId: loanProduct.id,
          loanProductSlug: loanProduct.slug,
        };
      });

      const waiversWithEntities = {
        waivers,
        loanProductSlugMap,
      };

      const json = JSON.stringify(waiversWithEntities);
      const blob = new Blob([json], { type: 'application/json' });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const nodeEnv = commonUtils.EnvUtils.getEnv();
      link.href = href;
      const envOnStage = nodeEnv === 'prod' ? '-' : `-${nodeEnv}-`;
      link.download = `${creditFacility.creditFacility.slug}${envOnStage}receivable-waivers.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [],
  );

  if (!creditFacilityData || !waiversData) {
    return <Skeleton />;
  }

  return (
    <MenuItem
      icon={<FiDownload />}
      onClick={() =>
        downloadWaiversWithEntitiesJson({
          waivers:
            waiversData.receivableWaiversForCreditFacilityWithOrganization,
          creditFacility: creditFacilityData,
        })
      }
    >
      Export receivable waivers JSON
    </MenuItem>
  );
};

type UploadWaiverJsonDropzoneProps = {
  setJSON: (values: React.SetStateAction<any>) => void;
};
const UploadWaiverJsonDropzone = ({
  setJSON,
}: UploadWaiverJsonDropzoneProps) => {
  const toast = useToast();
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept: { 'application/json': ['.json'] },
    multiple: false,
    onDrop: useCallback(
      (files: FileWithPath[]) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            finleyAssert(
              _.isString(reader.result),
              `reader.result should be string [Found:${typeof reader.result}]`,
            );
            const uploadJson = JSON.parse(reader.result);

            setJSON(uploadJson);
          } catch {
            if (!toast.isActive(errorToastId)) {
              errorToastAdminConfig.title = `Error parsing JSON.`;
              toast(errorToastAdminConfig);
            }
          }
        };
        reader.readAsText(files[0], 'utf-8');
      },
      [setJSON, toast],
    ),
  });

  const acceptedFileItems = _.map(acceptedFiles, (file: FileWithPath) => (
    <Text key={file.path}>{truncateUploadFileName(file.path)}</Text>
  ));

  const fileRejectionItems = _.map(
    fileRejections,
    ({ file }: { file: FileWithPath }) => (
      <>
        <Text key={file.path}>{truncateUploadFileName(file.path)}</Text>
        <Text>
          Expected file of type <em>json</em>
        </Text>
      </>
    ),
  );

  const fileSelected = !_.isEmpty(acceptedFiles) || !_.isEmpty(fileRejections);

  return (
    <Box width="100%" cursor="pointer">
      <Center
        border="1px"
        borderRadius="md"
        minHeight="200px"
        flexDirection="column"
        {...getRootProps({ className: 'dropzone' })}
      >
        <input {...getInputProps()} />
        {!_.isEmpty(acceptedFiles) && (
          <>
            <Heading size="sm">
              <Icon icon="file-plus" color="green.300" /> Accepted
            </Heading>
            {acceptedFileItems}
          </>
        )}
        {!_.isEmpty(fileRejections) && (
          <>
            <Flex alignItems="center">
              <Icon icon="file-minus" color="red.300" />
              <Heading size="sm">Rejected</Heading>
            </Flex>
            {fileRejectionItems}
          </>
        )}
        <Text
          textAlign="center"
          as={fileSelected ? 'sub' : 'i'}
          pt={fileSelected ? '20px' : '0'}
        >
          {`Drag a file here, or click to select${
            fileSelected ? ' a new' : ''
          } file`}
        </Text>
      </Center>
    </Box>
  );
};

type UploadJsonModalProps = {
  isOpen: boolean;
  onClose: () => void;
  creditFacilityId: string;
};
export const UploadJsonModal = ({
  isOpen,
  creditFacilityId,
  onClose,
}: UploadJsonModalProps) => {
  const [json, setJSON] = useState<{ [key: string]: any } | undefined>(
    undefined,
  );
  const [convertedWaiverInputs, setConvertedWaiverInputs] = useState<
    ReceivableBorrowingBaseTestWaiverInputGQLType[]
  >();

  const toast = useToast();

  const [updateWaivers] = useUpdateReceivableWaiversMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    onCompleted: (data) => {
      if (data) {
        successToastConfig.title = `Successfully updated waivers in the database`;
        toast(successToastConfig);
      }
    },
  });

  const [
    convertReceivableWaivers,
  ] = useConvertReceivableBorrowingBaseWaiversWithNewIdsLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    onCompleted: (data) => {
      try {
        if (data.convertReceivableBorrowingBaseWaiversWithNewIds) {
          const waiverInputsData = _.map(
            data.convertReceivableBorrowingBaseWaiversWithNewIds,
            (waiver) => {
              const {
                id: _id,
                created_at: _created_at,
                updated_at: _updated_at,
                lastUpdatedUserId: _lastUpdatedUserId,
                lastUpdatedOrganizationName: _lastUpdatedOrganizationName,
                __typename: _typename,
                ...waiverInputs
              } = waiver;

              return waiverInputs;
            },
          );
          setConvertedWaiverInputs(waiverInputsData);
        }
      } catch {
        if (!toast.isActive(errorToastId)) {
          errorToastAdminConfig.title = `Error parsing converted waivers JSON.`;
          toast(errorToastAdminConfig);
        }
      }
    },
  });

  const onCloseModal = () => {
    onClose();
    setConvertedWaiverInputs(undefined);
    setJSON(undefined);
  };

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal}>
      <ModalOverlay />
      <ModalContent padding="20px" minW="5xl" width="2xl">
        <ModalHeader>Upload</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Text>Upload Credit Agreement Full Artifact JSON</Text>
          <UploadWaiverJsonDropzone setJSON={setJSON} />
          <Flex justifyContent="center" mt="8">
            {!convertedWaiverInputs?.length && (
              <Button
                onClick={() => {
                  const jsonCopy = _.cloneDeep(json);

                  if (jsonCopy) {
                    convertReceivableWaivers({
                      variables: {
                        creditFacilityId,
                        waiversWithEntitiesJson: JSON.stringify(jsonCopy),
                      },
                    });
                  }
                }}
                aria-label="Upload Waivers"
              >
                Convert Waivers
              </Button>
            )}
            {convertedWaiverInputs && Boolean(convertedWaiverInputs.length) && (
              <Button
                onClick={() => {
                  updateWaivers({
                    variables: {
                      creditFacilityId,
                      receivableWaivers: convertedWaiverInputs,
                    },
                  });
                  setConvertedWaiverInputs(undefined);
                  onCloseModal();
                }}
                aria-label="Upload Waivers"
              >
                {`Insert ${convertedWaiverInputs.length} waivers into database`}
              </Button>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type WaiversExportImportMenuProps = {
  creditFacilityId: string;
};
export const WaiversExportImportMenu = ({
  creditFacilityId,
}: WaiversExportImportMenuProps) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  return (
    <>
      <Menu>
        <Tooltip label="Receivable waivers import / export">
          <MenuButton
            as={IconButton}
            aria-label="Receivable waivers"
            icon={<Icon icon="list-check" />}
            variant="ghost"
          />
        </Tooltip>
        <MenuList>
          <ExportWaiversButton creditFacilityId={creditFacilityId} />
          <MenuItem icon={<FiUpload />} onClick={onOpen}>
            Import receivable waivers from JSON
          </MenuItem>
        </MenuList>
      </Menu>
      <UploadJsonModal
        isOpen={isOpen}
        creditFacilityId={creditFacilityId}
        onClose={onClose}
      />
    </>
  );
};
